<template>
  <template v-if="!loading">
    <vl-grid v-vl-spacer:bottom.medium class="section">
      <vl-column width="10" width-xs="12">
        <vl-title tag-name="h3"> {{ $t('operationentity.detail.title') }} </vl-title>
      </vl-column>
      <vl-column v-if="editStore.canEdit" width="2" width-xs="12">
        <vl-action-group mod-align-right>
          <vl-button icon="plus" mod-icon-before :mod-disabled="isCreating" @click="onCreateButtonClick">{{
            $t('general.add')
          }}</vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <vl-grid :class="{ edit: isCreating }">
      <vl-column>
        <OperationEntityCreate v-if="isCreating" :specification-id="specificationId" />
      </vl-column>
    </vl-grid>
    <vl-grid v-if="items && items.length" v-vl-spacer:bottom.medium class="section">
      <vl-column width="12" width-xs="12">
        <vl-accordion-list>
          <vl-accordion-list-item v-for="(entity, i) in items" :id="i" :key="i">
            <vl-accordion :id="i.toString()" :title="getLabel(entity)">
              <vl-grid :class="{ edit: isEditing(entity.id) }">
                <vl-column width="8">
                  <OperationEntityEdit
                    v-if="isEditing(entity.id)"
                    ref="editForm"
                    :detail="entity"
                    :specification-id="specificationId"
                  />
                  <OperationEntityRead v-else :detail="entity" />
                </vl-column>
                <vl-column v-if="editStore.canEdit" width="4">
                  <vl-action-group v-if="!isEditing(entity.id)" mod-align-right mod-space between>
                    <vl-button
                      icon="pencil"
                      mod-icon-before
                      mod-secondary
                      :mod-disabled="editStore.editing"
                      @click="onEditButtonClick(entity.id)"
                    >
                      {{ $t('general.edit') }}
                    </vl-button>
                    <vl-button
                      v-if="!hasTransformation(entity.id)"
                      icon="trash"
                      mod-icon-before
                      mod-error
                      @click="onDeleteButtonClick(entity.id)"
                      >{{ $t('general.delete') }}</vl-button
                    >
                  </vl-action-group>
                  <vl-action-group v-else mod-align-right mod-space between>
                    <vl-button icon="save" x mod-icon-before @click="onSaveButtonClick">{{
                      $t('general.save')
                    }}</vl-button>
                    <vl-button icon="cross" mod-icon-before mod-secondary mod-error @click="onCancelSaveButtonClick">
                      {{ $t('general.cancel') }}
                    </vl-button>
                  </vl-action-group>
                </vl-column>
              </vl-grid>
            </vl-accordion>
          </vl-accordion-list-item>
        </vl-accordion-list>
      </vl-column>
    </vl-grid>
    <vl-grid v-else v-vl-spacer:bottom.medium class="section">
      <vl-column>
        <vl-annotation>{{ $t('operationentity.detail.empty') }}</vl-annotation>
      </vl-column>
    </vl-grid>
  </template>
</template>
<script lang="ts" setup>
import OperationEntityRead from './OperationEntityRead.vue';
import OperationEntityCreate from './OperationEntityCreate.vue';
import OperationEntityEdit from './OperationEntityEdit.vue';
import { OperationEntityDetailDto } from '@/api/backoffice/clients';
import { useOperationEntityStore } from '@/modules/configuration/store/operation-entity/operation-entity.store';
import {
  DeliveryProcessDefinitionEditType,
  useDeliveryProcessDefinitionEditStore
} from '@/modules/configuration/store/delivery-process-definition/delivery-process-definition-edit.store';
import { computed, onMounted, Ref, ref } from 'vue';
import { useDeliveryTransformationStore } from '@/modules/configuration/store/delivery-transformation/delivery-transformation.store';
import { useRoute } from 'vue-router';
import { useModalStore } from '@/modules/core/store/modal.store';
import { useI18n } from 'vue-i18n';

const store = useOperationEntityStore();
const transformationStore = useDeliveryTransformationStore();
const editStore = useDeliveryProcessDefinitionEditStore();
const modalStore = useModalStore();

const { t } = useI18n();
const route = useRoute();

const editForm: Ref<typeof OperationEntityEdit | (typeof OperationEntityEdit)[]> = ref(null);

const props = defineProps({
  specificationId: {
    type: Number,
    required: true
  }
});

const loading = computed((): boolean => {
  return store.loading;
});

const items = computed((): OperationEntityDetailDto[] => {
  return store.bySpecification(props.specificationId);
});

const isCreating = computed((): boolean => {
  return editStore.isCreate(DeliveryProcessDefinitionEditType.OperationEntity);
});

const isEditing = (entityId: number): boolean => {
  return editStore.isEdit(DeliveryProcessDefinitionEditType.OperationEntity, entityId);
};

const hasTransformation = (id: number): boolean => {
  return transformationStore.byDefinition(parseInt(route.params.id as string)).some((x) => x.toEntity?.id === id);
};

onMounted(async () => {
  if (props.specificationId) {
    await store.getBySpecification(props.specificationId);
  }
});

const onCreateButtonClick = () => {
  editStore.openCreate(DeliveryProcessDefinitionEditType.OperationEntity);
};

const onEditButtonClick = (entityId: number) => {
  editStore.openEdit(DeliveryProcessDefinitionEditType.OperationEntity, entityId);
};

const onDeleteButtonClick = (entityId: number) => {
  modalStore.showDeleteModal({
    title: t('operationentity.detail.delete.title'),
    props: {
      confirm: () => store.remove(props.specificationId, entityId)
    }
  });
};

const onSaveButtonClick = () => {
  if (Array.isArray(editForm?.value)) {
    editForm.value[0].onSubmit();
  } else {
    editForm.value.onSubmit();
  }
};

const onCancelSaveButtonClick = () => {
  editStore.closeEdit();
};

const getLabel = (item: OperationEntityDetailDto): string => {
  return `${item.code} - ${item.label}`;
};
</script>
<style lang="scss" scoped>
:deep(.vl-accordion__panel) {
  padding-right: 0;
}
.vl-grid {
  flex-grow: 1;
}
.section {
  padding-left: 20px;
}
</style>
